import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import messages from './data/ServiceMessages.json';
import { CATEGORY_META, fmt, daysUntil, usePageMeta } from './shared';
import Layout from './Layout';

export default function Timeline() {
  usePageMeta({
    title: 'Action Required Timeline | MS Message Center',
    description: 'View upcoming Microsoft 365 Message Center action-required deadlines in a chronological timeline. Never miss a compliance or migration deadline.',
    url: 'https://msmessagecenter.com/timeline',
  });

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // All action-required messages sorted chronologically
  const sorted = useMemo(() =>
    messages
      .filter(m => m.actionRequiredByDateTime)
      .sort((a, b) => new Date(b.actionRequiredByDateTime) - new Date(a.actionRequiredByDateTime)),
    []
  );

  const overdue  = sorted.filter(m => new Date(m.actionRequiredByDateTime) < today);
  const upcoming = sorted.filter(m => new Date(m.actionRequiredByDateTime) >= today);

  function groupByMonth(items) {
    const groups = new Map();
    items.forEach(m => {
      const d   = new Date(m.actionRequiredByDateTime);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const lbl = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      if (!groups.has(key)) groups.set(key, { key, label: lbl, items: [] });
      groups.get(key).items.push(m);
    });
    return [...groups.values()];
  }

  const upcomingGroups = groupByMonth(upcoming);

  function MessageItem({ m }) {
    const days    = daysUntil(m.actionRequiredByDateTime);
    const catMeta = CATEGORY_META[m.category] ?? { label: m.category, cls: 'bg-gray-100 text-gray-700 border-gray-200' };
    return (
      <li className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <Link
          to={`/${m.id}`}
          className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors group"
        >
          {/* Date pill */}
          <div className="shrink-0 text-center min-w-[76px]">
            <div className="text-xs text-gray-400 dark:text-gray-500">Action by</div>
            <div className="text-sm font-bold text-gray-800 dark:text-gray-100">{fmt(m.actionRequiredByDateTime)}</div>
            {days !== null && (
              <div className={`text-xs font-semibold mt-0.5 ${
                days < 0 ? 'text-red-500' :
                days === 0 ? 'text-red-600' :
                days <= 7 ? 'text-red-500' :
                days <= 30 ? 'text-orange-500' : 'text-gray-400 dark:text-gray-500'
              }`}>
                {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Today' : `${days}d left`}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="hidden sm:block w-px h-10 bg-gray-200 dark:bg-gray-700 shrink-0" />

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              <span className="text-xs font-mono font-semibold text-gray-400 bg-gray-100 dark:bg-gray-800 dark:text-gray-500 px-2 py-0.5 rounded">{m.id}</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded border ${catMeta.cls}`}>{catMeta.label}</span>
              {m.isMajorChange && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded bg-purple-100 text-purple-800 border border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700">★ Major</span>
              )}
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-700 dark:group-hover:text-blue-400 transition-colors line-clamp-2">{m.title}</p>
            {(m.services?.length ?? 0) > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {m.services.slice(0, 3).map(s => (
                  <span key={s} className="text-xs bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-700 px-2 py-0.5 rounded-full">{s}</span>
                ))}
                {m.services.length > 3 && <span className="text-xs text-gray-400">+{m.services.length - 3}</span>}
              </div>
            )}
          </div>

          {/* Arrow */}
          <svg className="w-4 h-4 text-gray-300 group-hover:text-blue-400 shrink-0 transition-colors ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </li>
    );
  }

  return (
    <Layout>
      <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-50 mb-2">Action Required Timeline</h1>
          <p className="text-base text-gray-600 dark:text-gray-400">
            Chronological view of all Microsoft 365 Message Center items with an action-required deadline.
          </p>
          <div className="mt-4 flex flex-wrap gap-5 text-sm text-gray-600 dark:text-gray-400">
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-red-500 inline-block" />Due within 7 days</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-orange-400 inline-block" />Due within 30 days</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-gray-300 dark:bg-gray-600 inline-block" />Due later</span>
          </div>
        </div>

        {sorted.length === 0 && (
          <p className="text-gray-500 dark:text-gray-400">No action-required messages found.</p>
        )}

        {/* Upcoming grouped by month */}
        {upcomingGroups.map(group => (
          <section key={group.key} className="mb-10">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />
              {group.label} ({group.items.length})
            </h2>
            <ul className="space-y-3">
              {group.items.map(m => <MessageItem key={m.id} m={m} />)}
            </ul>
          </section>
        ))}

        {/* Overdue */}
        {overdue.length > 0 && (
          <section className="mb-10">
            <h2 className="text-sm font-semibold text-red-600 dark:text-red-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
              Overdue ({overdue.length})
            </h2>
            <ul className="space-y-3">
              {overdue.map(m => <MessageItem key={m.id} m={m} />)}
            </ul>
          </section>
        )}

        {upcoming.length === 0 && overdue.length === 0 && sorted.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-400">No action-required messages found.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
